import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useToastContext } from 'context/ToastContext';
import {
  AgentApproval,
  AgentMetrics,
  RejectionReasonTag,
} from '@models/approval';
import {
  approveMessage,
  editAndApproveMessage,
  getApprovalTimeSeries,
  getApprovals,
  rejectMessage,
  retryMessage,
  subscribeToAgentMetrics,
} from '@util/firestore/support/support.service';

const APPROVALS_KEY = ['agent-approvals'];

export const useApprovals = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: APPROVALS_KEY,
    queryFn: () => getApprovals({ limit: 50 }),
  });

  return { approvals: data ?? [], isLoading, error, refetch };
};

export const useApproveMessage = () => {
  const queryClient = useQueryClient();
  const { showErrorToast } = useToastContext();

  const approveMutation = useMutation({
    mutationFn: ({
      approvalId,
      approvedActionIds,
      saveAsExample,
    }: {
      approvalId: string;
      approvedActionIds?: string[];
      saveAsExample?: boolean;
    }) => approveMessage({ approvalId, approvedActionIds, saveAsExample }),
    onMutate: async ({ approvalId }) => {
      await queryClient.cancelQueries({ queryKey: APPROVALS_KEY });
      const previous = queryClient.getQueryData<AgentApproval[]>(APPROVALS_KEY);
      queryClient.setQueryData<AgentApproval[]>(APPROVALS_KEY, (old) =>
        (old ?? []).filter((a) => a.id !== approvalId)
      );
      return { previous };
    },
    onError: (err, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(APPROVALS_KEY, context.previous);
      }
      showErrorToast((err as Error).message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: APPROVALS_KEY });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({
      approvalId,
      reason,
      reasonTags,
    }: {
      approvalId: string;
      reason?: string;
      reasonTags?: RejectionReasonTag[];
    }) => rejectMessage({ approvalId, reason, reasonTags }),
    onMutate: async ({ approvalId }) => {
      await queryClient.cancelQueries({ queryKey: APPROVALS_KEY });
      const previous = queryClient.getQueryData<AgentApproval[]>(APPROVALS_KEY);
      queryClient.setQueryData<AgentApproval[]>(APPROVALS_KEY, (old) =>
        (old ?? []).filter((a) => a.id !== approvalId)
      );
      return { previous };
    },
    onError: (err, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(APPROVALS_KEY, context.previous);
      }
      showErrorToast((err as Error).message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: APPROVALS_KEY });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({
      approvalId,
      editedMessage,
      approvedActionIds,
      saveAsExample,
    }: {
      approvalId: string;
      editedMessage: string;
      approvedActionIds?: string[];
      saveAsExample?: boolean;
    }) =>
      editAndApproveMessage({
        approvalId,
        editedMessage,
        approvedActionIds,
        saveAsExample,
      }),
    onMutate: async ({ approvalId }) => {
      await queryClient.cancelQueries({ queryKey: APPROVALS_KEY });
      const previous = queryClient.getQueryData<AgentApproval[]>(APPROVALS_KEY);
      queryClient.setQueryData<AgentApproval[]>(APPROVALS_KEY, (old) =>
        (old ?? []).filter((a) => a.id !== approvalId)
      );
      return { previous };
    },
    onError: (err, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(APPROVALS_KEY, context.previous);
      }
      showErrorToast((err as Error).message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: APPROVALS_KEY });
    },
  });

  const retryMutation = useMutation({
    mutationFn: ({
      approvalId,
      additionalInstructions,
    }: {
      approvalId: string;
      additionalInstructions: string;
    }) => retryMessage({ approvalId, additionalInstructions }),
    onMutate: async ({ approvalId }) => {
      await queryClient.cancelQueries({ queryKey: APPROVALS_KEY });
      const previous = queryClient.getQueryData<AgentApproval[]>(APPROVALS_KEY);
      queryClient.setQueryData<AgentApproval[]>(APPROVALS_KEY, (old) =>
        (old ?? []).filter((a) => a.id !== approvalId)
      );
      return { previous };
    },
    onError: (err, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(APPROVALS_KEY, context.previous);
      }
      showErrorToast((err as Error).message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: APPROVALS_KEY });
    },
  });

  return { approveMutation, rejectMutation, editMutation, retryMutation };
};

export const useApprovalTimeSeries = (
  rangeDays: number,
  bucket: 'day' | 'week' = 'week'
) => {
  const endMs = Date.now();
  const startMs = endMs - rangeDays * 24 * 60 * 60 * 1000;

  return useQuery({
    queryKey: ['agent-approval-timeseries', rangeDays, bucket],
    queryFn: () => getApprovalTimeSeries({ startMs, endMs, bucket }),
    staleTime: 60_000,
  });
};

export const usePendingCount = () => {
  const [metrics, setMetrics] = useState<AgentMetrics>({
    pending_count: 0,
    approved_count: 0,
    rejected_count: 0,
    edited_count: 0,
    auto_approved_count: 0,
  });

  useEffect(() => {
    const unsubscribe = subscribeToAgentMetrics(setMetrics);
    return unsubscribe;
  }, []);

  return metrics;
};
